import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { GeofenceAdjustmentService } from '@buspro/web/admin/standard-places';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { lastValueFrom } from 'rxjs';

/**
 * "Stop zones overlapping" — the needs-attention tile for geofence conflicts.
 *
 * Why it earns a place on the dashboard: an overlap costs a crossing its video AND its audit
 * evidence, it cannot be recovered afterwards, and nothing else announces it. The trip simply
 * sits in Collecting Media and someone wonders, days later, why a stop has no footage.
 *
 * It deliberately shows REPORTED and UNREPORTED separately. A tenant being blocked is not a
 * prerequisite for the problem existing — the worst case is a conflict nobody has hit yet,
 * quietly losing footage on every trip through those two stops.
 */
@Component({
  selector: 'buspro-admin-geofence-conflicts-widget',
  standalone: true,
  imports: [CommonModule, RouterModule, NzIconModule, NzToolTipModule],
  template: `
    <!-- Renders NOTHING when there is nothing to act on. A dashboard strip that says "all clear"
         every day costs space on every other day and trains the eye to skip it. -->
    @if (!summaryQuery.isPending() && total() > 0) {
      <a
        routerLink="/standard-places/geofence-adjustments"
        class="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-error/30 bg-error/5 px-4 py-2.5 no-underline transition-colors hover:bg-error/10">
        <span class="fe fe-alert-triangle text-error"></span>

        <span class="text-sm font-semibold text-base-content">
          {{ inConflict() }} stop {{ inConflict() === 1 ? 'zone overlaps' : 'zones overlap' }} another
        </span>

        @if (unreported() > 0) {
          <span class="text-xs text-base-content/60"
                nz-tooltip
                nzTooltipTitle="No tenant has reported these. Every trip through those stops is losing a stop's footage.">
            · {{ unreported() }} unreported
          </span>
        }
        @if (pending() > 0) {
          <span class="text-xs text-base-content/60">· {{ pending() }} awaiting your approval</span>
        }

        <span class="ml-auto text-sm font-medium text-error">Review &amp; fix →</span>
      </a>
    }
  `,
})
export class GeofenceConflictsWidgetComponent {
  private readonly service = inject(GeofenceAdjustmentService);

  readonly summaryQuery = injectQuery(() => ({
    queryKey: ['geofence-adjustment-summary'],
    queryFn: () => lastValueFrom(this.service.summary()),
  }));

  readonly inConflict = computed(() => this.summaryQuery.data()?.placesInConflict ?? 0);
  readonly unreported = computed(() => this.summaryQuery.data()?.unreportedPlaces ?? 0);
  readonly pending = computed(() => this.summaryQuery.data()?.pendingRequests ?? 0);
  readonly total = computed(() => this.inConflict() + this.pending());
}
